/**
 * Permissions Service
 *
 * Manages user permissions, franchises, and location data.
 * Centralized access to user permissions and related data throughout the application.
 */

import { reactive } from 'vue'
import { log } from '../utils/DebugLogger'
import { httpService } from './httpService'
import { tokenService } from './tokenService'
import { config } from './configService'
import { cacheService } from './cache'

export interface AuthUserData {
  sub: string
  username: string
  email: string
  email_verified?: boolean
  name?: string
  picture?: string
  groups: string[]
  language: string
  versaoSistema: number
}

export interface FranquiaDados {
  Nome: string
  Email?: string
  CNPJ?: string
  /** Free-text country as stored in the franquias registry: 'Brasil', 'BR', 'Chile'. */
  Pais?: string
  /**
   * ISO 3166-1 alpha-2, normalised server-side from `Pais`, or null when the
   * registry value maps to no known country.
   *
   * Use THIS for any country comparison. `Pais` is free text and is spelled out
   * for most franchises, so testing it against an ISO2 shape silently answers
   * "unknown" for a perfectly valid row.
   *
   * May be absent on a payload cached before this field existed (permissions
   * cache TTL is 2 h) — treat undefined as "fall back", never as "no country".
   */
  PaisISO?: string | null
  Idioma?: string
  [key: string]: unknown
}

export interface FuncionarioData {
  funcionario_id: string
  funcionario_nome: string
  funcionario_grupos: string[]
  /** Cognito Enabled — ausente = ativo (chamado 2026-0011). */
  funcionario_ativo?: boolean
  name?: string
  [key: string]: unknown
}

export interface SubaccountData {
  status?: string
  [key: string]: unknown
}

export interface FranquiaData {
  Dados: FranquiaDados
  Funcionarios: Record<string, FuncionarioData>
  AreasAtuacao: Record<string, Record<string, AreaAtuacaoItem>>
  Subaccount?: SubaccountData
  [key: string]: unknown
}

export interface AreaAtuacaoItem {
  ID: number
  Name: string
  CountryName?: string
  StateName?: string
  CityName?: string
  [key: string]: unknown
}

export interface PermissionsState {
  user: AuthUserData | null
  franquias: Record<string | number, FranquiaData>
  /** Capacidades nomeadas vindas do app-api (CapacidadesDoUsuario, skill app-groups) — a tela decide por elas, não por nome de grupo. */
  capacidades: string[]
  permissionsLoaded: boolean
  normalizedLocale: string
  isInitializing: boolean
  lastError: Error | null
}

export interface PermissionsData {
  user: AuthUserData | null
  franquias: Record<string | number, FranquiaData>
  capacidades: string[]
  locale: string
  permissionsLoaded: boolean
}

export interface InitializeOptions {
  minutesToRefresh?: number
  autoRefresh?: boolean
}

export interface SelectItem {
  ID: string | number
  Name: string
}

class LocationListItem {
  ID: number = 0
  Name: string = ''
  Type: string = ''
  TypeName: string = ''
  City: string = ''
  State: string = ''
  Country: string = ''
}

export const permissionsState: PermissionsState = reactive({
  user: null,
  franquias: {},
  capacidades: [],
  permissionsLoaded: false,
  normalizedLocale: 'pt-BR',
  isInitializing: false,
  lastError: null
})

class PermissionsService {
  private refreshTimer: ReturnType<typeof setTimeout> | null = null
  private minutesToRefresh: number = config.app.permissionsRefreshInterval

  async initialize(options: InitializeOptions = {}): Promise<PermissionsData> {
    const {
      minutesToRefresh = config.app.permissionsRefreshInterval,
      autoRefresh = config.app.permissionsAutoRefresh
    } = options

    this.minutesToRefresh = minutesToRefresh

    if (permissionsState.isInitializing) {
      log('PermissionsService', 'initialize', 'Already initializing, waiting...')
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (!permissionsState.isInitializing) {
            clearInterval(checkInterval)
            if (permissionsState.permissionsLoaded) {
              resolve(this.getPermissionsData())
            } else {
              reject(permissionsState.lastError || new Error('Permissions initialization failed'))
            }
          }
        }, 100)
      })
    }

    permissionsState.isInitializing = true
    permissionsState.lastError = null

    try {
      log('PermissionsService', 'initialize', 'Starting permissions initialization...')

      const permissionsData = await this.verificaPermissoesUsuario()
      log('PermissionsService', 'initialize', 'Permissions data received:', permissionsData)
      this.processPermissionsData(permissionsData)

      if (autoRefresh) {
        this.startRefreshTimer()
      }

      log('PermissionsService', 'initialize', 'Permissions initialization completed successfully')
      return this.getPermissionsData()

    } catch (error) {
      permissionsState.lastError = error as Error
      console.error('[PermissionsService][initialize] Error initializing permissions:', error)
      throw error
    } finally {
      permissionsState.isInitializing = false
    }
  }

  processPermissionsData(permissionsData: { Franquias?: Record<string, FranquiaData>; User?: AuthUserData; [key: string]: unknown } | null | undefined): void {
    log('PermissionsService', 'processPermissionsData', 'Processing permissions data...')

    if (permissionsData && permissionsData.Franquias) {
      permissionsState.franquias = permissionsData.Franquias
      permissionsState.user = permissionsData.User || null
      permissionsState.capacidades = Array.isArray(permissionsData.Capacidades) ? (permissionsData.Capacidades as string[]) : []

      const userLocale = permissionsData.User?.language || 'pt-BR'
      permissionsState.normalizedLocale = this.normalizeLocale(userLocale)

      permissionsState.permissionsLoaded = true

      log('PermissionsService', 'processPermissionsData', 'User language:', userLocale, 'normalized to:', permissionsState.normalizedLocale)
      log('PermissionsService', 'processPermissionsData', 'Franquias count:', Object.keys(permissionsState.franquias).length)

    } else {
      throw new Error('Invalid permissions data - missing Franquias')
    }
  }

  getPermissionsData(): PermissionsData {
    return {
      user: permissionsState.user,
      franquias: permissionsState.franquias,
      capacidades: permissionsState.capacidades,
      locale: permissionsState.normalizedLocale,
      permissionsLoaded: permissionsState.permissionsLoaded
    }
  }

  /**
   * Permissões NUNCA são persistidas no cliente (decisão do usuário,
   * 2026-09-01 — docs/plans/auth-state-jwt-like-sem-cache-local-plan.md).
   *
   * A cópia em IndexedDB era herança das views PHP (reload a cada clique).
   * Numa SPA o estado vive no singleton reativo durante a sessão, e um full
   * reload é justamente quando se QUER o estado fresco do backend — que já
   * cacheia permissões no memcached (2 h), então o custo é ~100-300 ms.
   * Persistir autorização no navegador produzia UI com permissão velha até
   * alguém apertar "limpar cache". Aproxima do padrão JWT: identidade e
   * autorização vêm do servidor, memória apenas.
   */
  async verificaPermissoesUsuario(): Promise<Record<string, unknown>> {
    log('PermissionsService', 'verificaPermissoesUsuario', 'Buscando permissões do backend (sem cache local)')
    return this.buscaPermissoesUsuario()
  }

  decodeJWT(token: string): Record<string, unknown> | null {
    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      log('PermissionsService', 'decodeJWT', 'Error decoding JWT:', error)
      return null
    }
  }

  async buscaPermissoesUsuario(): Promise<Record<string, unknown>> {
    try {
      log('PermissionsService', 'buscaPermissoesUsuario', 'Decoding JWT tokens for user data...')
      const idToken = tokenService.getIdToken()

      if (!idToken) {
        throw new Error('No ID token available - user not authenticated')
      }

      const decodedToken = this.decodeJWT(idToken)
      if (!decodedToken) {
        throw new Error('Failed to decode ID token')
      }
      log('PermissionsService', 'buscaPermissoesUsuario', 'Decoded ID token:', decodedToken)

      const authUserData: AuthUserData = {
        sub: decodedToken.sub as string,
        username: (decodedToken['cognito:username'] || decodedToken.username) as string,
        email: decodedToken.email as string,
        email_verified: decodedToken.email_verified as boolean | undefined,
        name: decodedToken.name as string | undefined,
        picture: decodedToken.picture as string | undefined,
        groups: (decodedToken['cognito:groups'] as string[]) || [],
        language: this.normalizeLocale(
          (decodedToken['custom:language'] as string) ||
          (decodedToken.locale as string) ||
          (decodedToken.language as string) ||
          'pt-BR'
        ),
        versaoSistema: Number(decodedToken['custom:versaoSistema'] || 0)
      }

      log('PermissionsService', 'buscaPermissoesUsuario', 'User data from JWT:', authUserData)

      log('PermissionsService', 'buscaPermissoesUsuario', 'Fetching franchise permissions from sistema...')
      const sistemaEndpoint = config.api.endpoints.permissions
      const sistemaData = await httpService.get<{
        success?: boolean
        dados?: { Franquias?: Record<string, FranquiaData>; Localidades?: Record<string, unknown>; IdiomaUsuario?: string; Capacidades?: string[] }
      }>(sistemaEndpoint, {
        logContext: 'PermissionsService.getSistemaPermissions',
        skipLoading: false
      })

      log('PermissionsService', 'buscaPermissoesUsuario', 'Sistema permissions received')

      let mergedData: { Success: boolean; Dados: Record<string, unknown> }

      if (sistemaData.success === true) {
        mergedData = {
          Success: true,
          Dados: {
            User: authUserData,
            Franquias: sistemaData.dados?.Franquias || {},
            Capacidades: sistemaData.dados?.Capacidades || [],
            Localidades: sistemaData.dados?.Localidades || {},
            IdiomaUsuario: authUserData.language || sistemaData.dados?.IdiomaUsuario || 'pt-BR',
            Expires: null
          }
        }
      } else {
        log('PermissionsService', 'buscaPermissoesUsuario', 'Sistema permissions failed, using JWT data only')
        mergedData = {
          Success: true,
          Dados: {
            User: authUserData,
            Franquias: {},
            Localidades: {},
            IdiomaUsuario: authUserData.language || 'pt-BR',
            Expires: null
          }
        }
      }

      const expires = new Date()
      expires.setMinutes(expires.getMinutes() + this.minutesToRefresh)
      const timestamp = Math.floor(expires.getTime() / 1000)
      mergedData.Dados.Expires = timestamp

      log('PermissionsService', 'buscaPermissoesUsuario', 'Merged data created:', mergedData.Dados)

      // Sem persistência local: o retorno alimenta apenas o estado reativo
      // em memória (processPermissionsData).
      return mergedData.Dados
    } catch (error) {
      log('PermissionsService', 'buscaPermissoesUsuario', 'Error fetching permissions', error)
      throw error
    }
  }

  startRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
    }

    this.refreshTimer = setTimeout(() => {
      this.refreshPermissions()
    }, this.minutesToRefresh * 60 * 1000)

    log('PermissionsService', 'startRefreshTimer', `Auto-refresh timer set for ${this.minutesToRefresh} minutes`)
  }

  async refreshPermissions(): Promise<void> {
    try {
      log('PermissionsService', 'refreshPermissions', 'Refreshing permissions...')
      const permissionsData = await this.buscaPermissoesUsuario()
      this.processPermissionsData(permissionsData as { Franquias?: Record<string, FranquiaData>; User?: AuthUserData })
      this.startRefreshTimer()
      log('PermissionsService', 'refreshPermissions', 'Permissions refreshed successfully')
    } catch (error) {
      console.error('[PermissionsService][refreshPermissions] Error refreshing permissions:', error)
      this.startRefreshTimer()
    }
  }

  stopRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
      log('PermissionsService', 'stopRefreshTimer', 'Auto-refresh timer stopped')
    }
  }

  getCurrentLocale(): string {
    return permissionsState.normalizedLocale
  }

  setLocale(locale: string): void {
    const normalized = this.normalizeLocale(locale)
    permissionsState.normalizedLocale = normalized
    log('PermissionsService', 'setLocale', 'Locale updated:', { original: locale, normalized })
  }

  normalizeLocale(locale: string): string {
    const locales: Record<string, string> = {
      'PT': 'pt-BR',
      'ES': 'es-ES',
      'ENG': 'en-US',
      'ENGUK': 'en-GB',
      'PTpt': 'pt-PT',
      'es-GT': 'es-GT',
      'es-EC': 'es-EC',
      // pt-AO tinha só auto-tradução do Google (lixo p/ siglas: PDV -> "CUBA"),
      // e um token com locale pt-AO fazia a 1ª carga pós-login vir toda quebrada
      // (F5 caía no pt-BR e funcionava). Colapsa para o pt-BR curado.
      'pt-AO': 'pt-BR',
    }
    return locales[locale] ?? locale
  }

  getUser(): AuthUserData | null {
    return permissionsState.user
  }

  isUserInGroups(groups: string[] = []): boolean {
    if (!permissionsState.user || !permissionsState.user.groups) return false

    for (const i in groups) {
      if (permissionsState.user.groups.includes(groups[i])) {
        return true
      }
    }

    return false
  }

  /** Capacidade nomeada do app-api (CapacidadesDoUsuario, skill app-groups): decida por ela, não por nome de grupo. */
  hasCapability(capability: string): boolean {
    return permissionsState.capacidades.includes(capability)
  }

  getUserGroups(): string[] {
    return permissionsState.user ? permissionsState.user.groups : []
  }

  getUserGroup(): string {
    if (!permissionsState.user || !this.getUserGroups()) return ''

    const groups = this.getUserGroups()
    if (groups.includes('Full')) return 'Full'
    if (groups.includes('Franqueado')) return 'Franqueado'
    if (groups.includes('Gerente')) return 'Gerente'
    if (groups.includes('Operador')) return 'Operador'
    if (groups.includes('Atendente')) return 'Atendente'
    if (groups.includes('Cliente')) return 'Cliente'

    return ''
  }

  getLocationsSelectArrayByFranquiaID(ID: number = 0): SelectItem[] {
    const locations = this.getLocationsByID(ID)
    const output: SelectItem[] = []

    for (const i in locations) {
      output.push({
        ID: `${locations[i].ID}-${locations[i].Type}`,
        Name: `${locations[i].Name} - ${locations[i].TypeName}`
      })
    }

    return output
  }

  getLocationsByID(ID: number = 0): LocationListItem[] {
    const output: LocationListItem[] = []
    const areasAtuacao = permissionsState.franquias[ID] ? permissionsState.franquias[ID].AreasAtuacao : {}

    // Somente as quatro listas de localidade. O payload carrega outros campos
    // (OwnerID, OwnerType, …) e um for...in cego sobre eles gerava itens
    // fantasma: iterar a string "franquia" (OwnerType) produzia 8 entradas
    // vazias — os "-" no select de Localização (bug 2026-09-01).
    const LOCATION_TYPES = ['Neighborhoods', 'Cities', 'States', 'Countries'] as const

    for (const type of LOCATION_TYPES) {
      for (const l in areasAtuacao[type]) {
        const t = areasAtuacao[type]
        const loc = new LocationListItem()

        if (type === 'Neighborhoods') {
          loc.Name = t[l].Name
          loc.ID = t[l].ID
          loc.Type = 'neighborhood'
          loc.TypeName = 'Bairro'
          loc.Country = t[l].CountryName || ''
          loc.State = t[l].StateName || ''
          loc.City = t[l].CityName || ''
        } else if (type === 'Cities') {
          loc.Name = t[l].Name
          loc.ID = t[l].ID
          loc.Type = 'city'
          loc.TypeName = 'Cidade'
          loc.Country = t[l].CountryName || ''
          loc.State = t[l].StateName || ''
        } else if (type === 'States') {
          loc.Name = t[l].Name
          loc.ID = t[l].ID
          loc.Type = 'state'
          loc.TypeName = 'Estado'
          loc.Country = t[l].CountryName || ''
        } else if (type === 'Countries') {
          loc.Name = t[l].Name
          loc.ID = t[l].ID
          loc.Type = 'country'
          loc.TypeName = 'País'
        }

        output.push(loc)
      }
    }

    return output
  }

  /**
   * Regra única de "inativo" (chamado 2026-0011): usuário desabilitado no
   * Cognito não aparece em NENHUM select/lista de escolha. Ausente = ativo.
   * `keepId` mantém quem já está gravado no registro em edição, para o
   * histórico não perder o nome. Buscas por id (getFuncionariosByFranquiaID,
   * getUserBySub) continuam vendo todo mundo.
   */
  private isSelecionavel(f: FuncionarioData, keepId: string): boolean {
    return f.funcionario_ativo !== false || (keepId !== '' && f.funcionario_id === keepId)
  }

  getAtendentesSelectArrayByFranquiaID(ID: number = 0, keepId: string = ''): SelectItem[] {
    const dados = this.getAtendentesByFranquiaID(ID, keepId)
    return dados.map(d => ({ ID: d.funcionario_id, Name: d.funcionario_nome }))
  }

  getAtendentesByFranquiaID(ID: number = 0, keepId: string = ''): FuncionarioData[] {
    const funcionarios = this.getFuncionariosByFranquiaID(ID)
    const output: FuncionarioData[] = []

    for (const a in funcionarios) {
      const f = funcionarios[a]
      if (!this.isSelecionavel(f, keepId)) continue
      if (
        f.funcionario_grupos.includes('Franqueado') ||
        f.funcionario_grupos.includes('Gerente') ||
        f.funcionario_grupos.includes('Atendente')
      ) {
        output.push(f)
      }
    }

    return output
  }

  /**
   * Operadores ATIVOS da franquia para um select. `keepId` mantém o operador
   * já gravado num registro mesmo que ele tenha sido desativado depois — o
   * histórico continua mostrando o nome certo (chamado 2026-0011).
   */
  getOperadoresSelectArrayByFranquiaID(ID: number = 0, keepId: string = ''): SelectItem[] {
    const dados = this.getOperadoresByFranquiaID(ID, keepId)
    const output: SelectItem[] = []
    for (const i in dados) {
      output.push({ ID: dados[i].funcionario_id, Name: dados[i].funcionario_nome })
    }
    return output
  }

  getOperadoresByFranquiaID(ID: number = 0, keepId: string = ''): Record<string, FuncionarioData> {
    const funcionarios = this.getFuncionariosByFranquiaID(ID)
    const output: Record<string, FuncionarioData> = {}

    for (const a in funcionarios) {
      const f = funcionarios[a]
      if (!f.funcionario_grupos.includes('Operador')) continue
      if (this.isSelecionavel(f, keepId)) {
        output[f.funcionario_id] = f
      }
    }

    return output
  }

  /** Todos os funcionários ATIVOS da franquia para um select (ver isSelecionavel). */
  getFuncionariosSelectArrayByFranquiaID(ID: number = 0, keepId: string = ''): SelectItem[] {
    const dados = this.getFuncionariosByFranquiaID(ID)
    const output: SelectItem[] = []
    for (const i in dados) {
      if (!this.isSelecionavel(dados[i], keepId)) continue
      output.push({ ID: dados[i].funcionario_id, Name: dados[i].funcionario_nome })
    }
    return output
  }

  getFuncionariosByFranquiaID(ID: number = 0): Record<string, FuncionarioData> {
    if (typeof permissionsState.franquias[ID] === 'undefined') {
      return {}
    }
    return permissionsState.franquias[ID].Funcionarios
  }

  getFranquiasSelect(): Record<string, string> {
    const franquias = permissionsState.franquias
    const output: Record<string, string> = {}

    for (const a in franquias) {
      output[a] = franquias[a].Dados.Nome
    }

    return output
  }

  getFranquiasSelectArray(): SelectItem[] {
    const franquias = permissionsState.franquias
    const output: SelectItem[] = []

    for (const a in franquias) {
      output.push({ ID: a, Name: franquias[a].Dados.Nome })
    }

    return output
  }

  hasPermission(permission: string): boolean {
    return this.isUserInGroups([permission])
  }

  canAccessFranchise(franchiseId: string | number): boolean {
    return !!(permissionsState.franquias && permissionsState.franquias[franchiseId])
  }

  getFranchiseName(franchiseId: string | number): string {
    return permissionsState.franquias && permissionsState.franquias[franchiseId]
      ? permissionsState.franquias[franchiseId].Dados.Nome
      : ''
  }

  getFranquiaById(franchiseId: string | number): FranquiaData | null {
    if (!permissionsState.franquias || !permissionsState.franquias[franchiseId]) {
      return null
    }
    return permissionsState.franquias[franchiseId]
  }

  getLocationsByID_Raw(ID: number = 0): Record<string, Record<string, AreaAtuacaoItem>> {
    return permissionsState.franquias[ID] ? permissionsState.franquias[ID].AreasAtuacao : {}
  }

  getFranquias(): Record<string | number, FranquiaData> {
    return permissionsState.franquias
  }

  getUserBySub(sub: string): FuncionarioData | null {
    if (!sub) return null

    const franquias = permissionsState.franquias

    for (const franchiseId in franquias) {
      const funcionarios = franquias[franchiseId].Funcionarios

      for (const funcionarioId in funcionarios) {
        const funcionario = funcionarios[funcionarioId]

        if (funcionario.funcionario_id === sub || funcionarioId === sub) {
          return funcionario
        }
      }
    }

    return null
  }

  getUserNameBySub(sub: string): string {
    const user = this.getUserBySub(sub)
    return user ? (user.funcionario_nome || user.name || '') : ''
  }

  getSubaccountByFranquiaID(franquiaId: number | string): SubaccountData | null {
    const franquia = permissionsState.franquias[franquiaId]
    return franquia?.Subaccount || null
  }

  getFranquiasWithSubaccounts(): Array<{ franquiaId: number; franquiaNome: string | undefined; subaccount: SubaccountData | null }> {
    return Object.entries(permissionsState.franquias)
      .map(([id, franquia]) => ({
        franquiaId: parseInt(id),
        franquiaNome: franquia.Dados?.Nome,
        subaccount: franquia.Subaccount || null
      }))
  }

  hasActiveSubaccount(franquiaId: number | string): boolean {
    const subaccount = this.getSubaccountByFranquiaID(franquiaId)
    return subaccount?.status === 'active'
  }

  getFranquiasWithActiveSubaccounts(): number[] {
    return Object.entries(permissionsState.franquias)
      .filter(([, franquia]) => franquia.Subaccount?.status === 'active')
      .map(([id]) => parseInt(id))
  }

  async clearAllData(): Promise<void> {
    log('PermissionsService', 'clearAllData', '🗑️ Purging all permissions data...')

    this.stopRefreshTimer()

    // Limpeza defensiva: permissões não são mais persistidas, mas sessões
    // antigas (antes de 2026-09-01) podem ter a entrada no IndexedDB.
    await cacheService.invalidate('permissions')

    permissionsState.user = null
    permissionsState.franquias = {}
    permissionsState.permissionsLoaded = false
    permissionsState.normalizedLocale = 'pt-BR'
    permissionsState.isInitializing = false
    permissionsState.lastError = null

    log('PermissionsService', 'clearAllData', '✅ All permissions data purged')
  }
}

export const permissionsService = new PermissionsService()
export { PermissionsService, LocationListItem }
export default permissionsService
